import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { MobileNav } from '../components/MobileNav';
import { Sparkles, Calendar, Tag, ArrowLeft, Share2, Loader2 } from 'lucide-react';
import { Footer } from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen swirl-bg flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#8B5CF6] animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen swirl-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-mystical text-3xl text-[#2D1B3D] mb-4">Post Not Found</h2>
          <Button onClick={() => navigate('/blog')} className="rounded-full btn-magic">
            <ArrowLeft className="mr-2 w-5 h-5" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen swirl-bg">
      <MobileNav />

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#8B5CF6] font-semibold hover:text-[#D4AF37] transition-colors mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Blog
          </Link>
        </motion.div>

        <article>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {/* Category & Date */}
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-flex items-center gap-1 bg-[#8B5CF6]/10 text-[#8B5CF6] font-semibold px-3 py-1 rounded-full">
                <Tag className="w-4 h-4" />
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1 text-[#8C7A9C]">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-mystical text-4xl md:text-5xl text-[#2D1B3D] font-bold mb-6">
              {post.title}
            </h1>

            {/* Share Button */}
            <button
              onClick={sharePost}
              className="inline-flex items-center gap-2 text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="share-post"
            >
              <Share2 className="w-5 h-5" />
              Share this post
            </button>
          </motion.div>

          {/* Featured Image */}
          {post.image_url && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-10 rounded-3xl overflow-hidden shadow-xl"
            >
              <img 
                src={post.image_url} 
                alt={post.title}
                className="w-full h-auto"
              />
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="ethereal-card rounded-3xl p-8 md:p-12"
          >
            <div 
              className="prose prose-lg max-w-none text-[#3D2A4D] 
                prose-headings:font-mystical prose-headings:text-[#2D1B3D] 
                prose-p:leading-relaxed prose-p:text-[#4A3A5A]
                prose-a:text-[#8B5CF6] prose-a:no-underline hover:prose-a:text-[#D4AF37]
                prose-strong:text-[#2D1B3D]
                prose-ul:text-[#4A3A5A] prose-ol:text-[#4A3A5A]
                prose-blockquote:border-l-[#8B5CF6] prose-blockquote:bg-[#8B5CF6]/5 prose-blockquote:rounded-r-xl prose-blockquote:py-2"
              dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/>') }}
              data-testid="blog-post-content"
            />
          </motion.div>

          {/* Author Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-10 ethereal-card rounded-3xl p-8 flex flex-col md:flex-row items-center gap-6"
          >
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#D4AF37] flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_soul-match-numbers/artifacts/lem5lq0f_IMG_4333.jpeg" 
                alt="Karine Burt"
                className="w-full h-full object-cover"
                style={{ objectPosition: '50% 25%' }}
              />
            </div>
            <div className="text-center md:text-left">
              <p className="text-[#8B5CF6] font-semibold mb-1">Written by</p>
              <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-2">Karine Burt</h3>
              <p className="text-[#5D4A6D]">Psychic Medium, Numerology Matchmaker & Spiritual Coach</p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-10 text-center"
          >
            <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-4">
              Ready to Discover Your Numbers?
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/calculator" data-testid="cta-calculator">
                <Button 
                  variant="outline" 
                  className="rounded-full px-6 py-4 font-bold border-2 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
                >
                  Try Soulmate Calculator
                </Button>
              </Link>
              <Link to="/contact" data-testid="cta-contact">
                <Button className="rounded-full btn-magic px-6 py-4 font-bold">
                  Book a Session
                </Button>
              </Link>
            </div>
          </motion.div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
